import React from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import type { Unit, UnitType } from "@/lib/types/unit";
import { InfoPanelType, MovementType, Coalition } from "@/lib/types/unit";
import { Country } from "@/lib/types/deck-builder";
import { createSortableHeader, type FilterType } from "@/components/DataTable";
import type { FilterFn } from "@tanstack/react-table";
import { createFilterString, createFilterNumber, createFilterEnum } from "@/components/DataTable";
import { TraitBadgeList } from "@/components/ui/trait-badge-list";
import { getDivisionImagePath, getCoalitionFlagPath, getCountryFlagPath } from "@/lib/utils/division-images";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { filterTraits } from "@/lib/utils/trait-icons";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { formatDistance } from "@/lib/utils/format-distance";
import { formatTime } from "@/lib/utils/format-time";

// Column labels
export const UNIT_COLUMN_LABELS: Record<string, string> = {
  name: "Name",
  id: "ID",
  descriptorName: "Descriptor Name",
  weaponDescriptorName: "Weapon Descriptor",
  factoryDescriptor: "Factory Descriptor",
  commandPoints: "CP",
  unitType: "Unit Type",
  nationality: "Nationality",
  motherCountry: "Mother Country",
  formation: "Formation",
  infoPanelType: "Info Panel Type",
  frontArmor: "Front Armor",
  sideArmor: "Side Armor",
  rearArmor: "Rear Armor",
  topArmor: "Top Armor",
  era: "ERA",
  maxDamage: "Max Damage",
  speed: "Speed",
  roadSpeed: "Road Speed",
  rotationTime: "Rotation Time",
  movementType: "Movement Type",
  occupiableTerrains: "Occupiable Terrains",
  optics: "Optics",
  airOptics: "Air Optics",
  lowAltOptics: "Low Alt Optics",
  opticsRange: "Optics Range",
  airOpticsRange: "Air Optics Range",
  lowAltOpticsRange: "Low Alt Optics Range",
  stealth: "Stealth",
  ecm: "ECM",
  fuel: "Fuel",
  fuelMove: "Fuel Move",
  supply: "Supply",
  advancedDeployment: "Advanced Deployment",
  agility: "Agility",
  flyingAltitude: "Flying Altitude",
  travelTime: "Travel Time",
  dangerousness: "Dangerousness",
  specialities: "Traits",
  bombStrategy: "Bomb Strategy",
  xpBonuses: "XP Bonuses",
  divisions: "Divisions",
  isCommand: "Is Command",
  isSpecialForces: "Is Special Forces",
  isSellable: "Is Sellable",
};

// Column type mapping
export const UNIT_COLUMN_TYPES: Record<string, FilterType> = {
  // Strings
  name: "string",
  descriptorName: "string",
  weaponDescriptorName: "string",
  factoryDescriptor: "string",
  nationality: "string",
  motherCountry: "string",
  formation: "string",
  bombStrategy: "string",
  xpBonuses: "string",
  // Numbers
  id: "number",
  commandPoints: "number",
  frontArmor: "number",
  sideArmor: "number",
  rearArmor: "number",
  topArmor: "number",
  maxDamage: "number",
  speed: "number",
  roadSpeed: "number",
  rotationTime: "number",
  optics: "number",
  airOptics: "number",
  lowAltOptics: "number",
  opticsRange: "number",
  airOpticsRange: "number",
  lowAltOpticsRange: "number",
  stealth: "number",
  ecm: "number",
  fuel: "number",
  fuelMove: "number",
  supply: "number",
  advancedDeployment: "number",
  agility: "number",
  flyingAltitude: "number",
  travelTime: "number",
  dangerousness: "number",
  // Enums
  coalition: "enum",
  country: "enum",
  infoPanelType: "enum",
  movementType: "enum",
  divisions: "enum",
  specialities: "enum",
  // Strings (additional)
  occupiableTerrains: "string",
  // Booleans
  era: "boolean",
  isCommand: "boolean",
  isSpecialForces: "boolean",
  isSellable: "boolean",
};

// Trait enum values mapping (trait names from trait-icon map)
// Display name (key) -> trait name (value)
export const TRAIT_ENUM_VALUES: Record<string, string> = {
  "Airlift": "_airlift",
  "Amphibious": "_amphibie",
  "Can Be Airlifted": "_canBeAirlifted",
  "Electronic Warfare": "_electronic_warfare",
  "EO Dazzler": "_eo_dazzler",
  "ERA": "_era",
  "False Flag": "_falseflag",
  "Fire Direction": "_fireDirection",
  "GSR": "_gsr",
  "Counter-Battery Radar": "_cbr",
  "Instructor": "_instructor",
  "Jammer": "_jammer",
  "Militia": "_militia",
  "Mountaineer": "_mountaineer",
  "MP": "_mp",
  "Para": "_para",
  "Resolute": "_resolute",
  "Security": "_security",
  "Special Forces": "_sf",
  "SIGINT": "_singint",
  "Smoke Launcher": "_smoke_launcher",
  "Sniper": "_sniper",
  "Transport": "_transport1",
  "Prime Mover": "_transport2",
  "Shock": "_choc",
  "IFV": "_ifv",
  "Leader": "_leader",
  "Recon": "_reco",
  "Reservist": "_reservist",
};

// Country enum values mapping (display name -> country code)
const COUNTRY_ENUM_VALUES: Record<string, string> = {
  "Belgium": Country.BEL,
  "Canada": Country.CAN,
  "France": Country.FR,
  "United Kingdom": Country.UK,
  "West Germany": Country.RFA,
  "United States": Country.US,
  "Soviet Union": Country.SOV,
  "East Germany": Country.DDR,
  "Spain": Country.ESP,
  "Poland": Country.POL,
  "Czechoslovakia": Country.TCH,
  "Netherlands": Country.NL,
  "Luxembourg": Country.LUX,
  "Denmark": Country.DK,
};

// Enum value mappings
export const UNIT_ENUM_VALUES: Record<string, Record<string, string>> = {
  coalition: Coalition,
  country: COUNTRY_ENUM_VALUES,
  infoPanelType: InfoPanelType,
  movementType: MovementType,
  specialities: TRAIT_ENUM_VALUES,
};

// Filter functions
const filterString = createFilterString<Unit>();
const filterNumber = createFilterNumber<Unit>();
const filterEnum = createFilterEnum<Unit>();

// Custom filter function for divisions array
const filterDivisions: FilterFn<Unit> = (row, columnId, filterValue: string) => {
  const divisions = row.original.divisions;
  
  // Special case: filter for empty/null values
  if (filterValue === "__empty__") {
    return !divisions || divisions.length === 0;
  }
  
  // Normal case: check if divisions array includes the filter value
  if (!divisions || divisions.length === 0) return false;
  // filterValue is the division descriptor
  return divisions.includes(filterValue);
};

// Custom filter function for specialities/traits array
const filterSpecialities: FilterFn<Unit> = (row, columnId, filterValue: string) => {
  const specialities = row.original.specialities;
  const traits = filterTraits(specialities || []);
  
  // Special case: filter for empty/null values
  if (filterValue === "__empty__") {
    return traits.length === 0;
  }
  
  // Normal case: check if traits array includes the filter value
  // filterValue is the trait name (e.g., "_choc", "_sf")
  return traits.includes(filterValue);
};

// Custom filter function for coalition enum
const filterCoalition: FilterFn<Unit> = (row, columnId, filterValue: string) => {
  const coalition = row.original.unitType?.coalition;
  
  // Special case: filter for empty/null values
  if (filterValue === "__empty__") {
    return !coalition;
  }
  
  // Normal case: match exact coalition value
  return coalition === filterValue;
};

// Custom filter function for country enum
const filterCountry: FilterFn<Unit> = (row, columnId, filterValue: string) => {
  const country = row.original.unitType?.country;
  
  // Special case: filter for empty/null values
  if (filterValue === "__empty__") {
    return !country || country === "";
  }
  
  // Normal case: match exact country value
  return country === filterValue;
};

// Helper function to render armor cell with type
function renderArmorCell(armor: number, armorType?: string) {
  return (
    <div>
      {armor} {armorType && `(${armorType})`}
    </div>
  );
}

// Helper function to render boolean values
function renderBoolean(value: boolean | undefined) {
  if (value === undefined) return <span className="text-muted-foreground">-</span>;
  return value ? "Yes" : "No";
}

// Helper function to render enum values
function renderEnum(value: string | undefined, enumType: Record<string, string>) {
  if (!value) return <span className="text-muted-foreground">-</span>;
  return enumType[value] || value;
}

// Helper function to render array values
function renderArray(value: string[] | undefined) {
  if (!value || value.length === 0) return <span className="text-muted-foreground">-</span>;
  return value.join(", ");
}

// Helper function to render unit type object
function renderUnitType(unitType: UnitType | undefined) {
  if (!unitType) return <span className="text-muted-foreground">-</span>;
  return `${unitType.coalition} / ${unitType.country} / ${unitType.formation}`;
}

// Helper function to render optional string/number values
function renderOptionalValue(value: string | number | undefined | null) {
  if (value === undefined || value === null || value === "") {
    return <span className="text-muted-foreground">-</span>;
  }
  return String(value);
}

// Helper function to calculate column width based on label length
function calculateColumnWidth(label: string): number {
  const charWidth = 8.5; // pixels per character
  const padding = 24; // total left/right padding
  const sortIconSpace = 24; // space for sort indicator
  const minWidth = 80; // minimum width for very short labels
  
  return Math.max(minWidth, label.length * charWidth + padding + sortIconSpace);
}

// Helper function to create a simple sortable column
function createSortableColumn(
  accessorKey: keyof Unit,
  label: string,
  options?: { 
    cell?: (value: unknown, unit: Unit) => React.ReactNode;
    filterFn?: FilterFn<Unit>;
    size?: number;
    meta?: Record<string, unknown>;
  }
): ColumnDef<Unit> {
  const columnDef: ColumnDef<Unit> = {
    accessorKey,
    header: createSortableHeader(label),
  };
  
  // Set size: use explicit size if provided, otherwise calculate from label
  if (options?.size !== undefined) {
    columnDef.size = options.size;
  } else {
    columnDef.size = calculateColumnWidth(label);
  }
  
  if (options?.cell) {
    columnDef.cell = ({ row }) => options.cell!(row.getValue(accessorKey), row.original);
  }

  if (options?.filterFn) {
    columnDef.filterFn = options.filterFn;
  } else {
    const filterType = UNIT_COLUMN_TYPES[accessorKey];
    if (filterType === "number") {
      columnDef.filterFn = filterNumber;
    } else if (filterType === "enum") {
      columnDef.filterFn = filterEnum;
    } else {
      columnDef.filterFn = filterString;
    }
  }
  
  if (options?.meta) {
    columnDef.meta = options.meta;
  }
  
  return columnDef;
}

// Helper function to create a sortable column with custom accessor
function createSortableColumnWithAccessor(
  id: string,
  accessorFn: (row: Unit) => unknown,
  label: string,
  options?: { 
    cell?: (unit: Unit) => React.ReactNode;
    filterFn?: FilterFn<Unit>;
    size?: number;
    meta?: Record<string, unknown>;
  }
): ColumnDef<Unit> {
  const columnDef: ColumnDef<Unit> = {
    id,
    accessorFn,
    header: createSortableHeader(label),
  };
  
  // Set size: use explicit size if provided, otherwise calculate from label
  if (options?.size !== undefined) {
    columnDef.size = options.size;
  } else {
    columnDef.size = calculateColumnWidth(label);
  }
  
  if (options?.cell) {
    columnDef.cell = ({ row }) => options.cell!(row.original);
  }

  if (options?.filterFn) {
    columnDef.filterFn = options.filterFn;
  } else {
    const filterType = UNIT_COLUMN_TYPES[id];
    if (filterType === "number") {
      columnDef.filterFn = filterNumber;
    } else if (filterType === "enum") {
      columnDef.filterFn = filterEnum;
    } else {
      columnDef.filterFn = filterString;
    }
  }
  
  if (options?.meta) {
    columnDef.meta = options.meta;
  }
  
  return columnDef;
}

// Column definitions - all Unit fields
export function createUnitColumns(): ColumnDef<Unit>[] {
  return [
    // Basic Info
    createSortableColumn("name", "Name", {
      size: 250,
      meta: { sticky: true },
      cell: (value, unit) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={`/units/${encodeURIComponent(unit.descriptorName)}`}
              className="font-medium text-primary hover:underline"
            >
              {value as string}
            </Link>
          </TooltipTrigger>
          <TooltipContent className="p-2">
            <img
              src={getUnitImagePath(unit.descriptorName)}
              alt={unit.name}
              className="max-w-[200px] max-h-[200px] object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </TooltipContent>
        </Tooltip>
      ),
    }),
    createSortableColumn("id", "ID"),
    createSortableColumn("descriptorName", "Descriptor Name"),
    createSortableColumn("weaponDescriptorName", "Weapon Descriptor"),
    createSortableColumn("factoryDescriptor", "Factory Descriptor"),
    createSortableColumn("commandPoints", "CP"),
    
    // Unit Type (removed - coalition and country columns provide this info)
    createSortableColumnWithAccessor(
      "coalition",
      (row) => row.unitType?.coalition || "",
      "Coalition",
      {
        cell: (unit) => {
          const coalition = unit.unitType?.coalition;
          if (!coalition) {
            return <span className="text-muted-foreground">-</span>;
          }
          const flagPath = getCoalitionFlagPath(coalition);
          if (!flagPath) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <img
              src={flagPath}
              alt={coalition}
              className="h-5 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          );
        },
        filterFn: filterCoalition,
      }
    ),
    createSortableColumnWithAccessor(
      "country",
      (row) => row.unitType?.country || "",
      "Country",
      {
        cell: (unit) => {
          const country = unit.unitType?.country;
          if (!country) {
            return <span className="text-muted-foreground">-</span>;
          }
          const flagPath = getCountryFlagPath(country);
          if (!flagPath) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <img
              src={flagPath}
              alt={country}
              className="h-5 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          );
        },
        filterFn: filterCountry,
      }
    ),
    createSortableColumnWithAccessor(
      "formation",
      (row) => row.unitType?.formation || "",
      "Formation"
    ),
    
    // Info Panel Type
    createSortableColumnWithAccessor(
      "infoPanelType",
      (row) => row.infoPanelType,
      "Info Panel Type",
      {
        cell: (unit) => renderEnum(unit.infoPanelType, InfoPanelType),
      }
    ),
    
    // Armor
    createSortableColumnWithAccessor(
      "frontArmor",
      (row) => row.frontArmor,
      "Front Armor",
      {
        cell: (unit) => renderArmorCell(unit.frontArmor, unit.frontArmorType),
      }
    ),
    createSortableColumnWithAccessor(
      "sideArmor",
      (row) => row.sideArmor,
      "Side Armor",
      {
        cell: (unit) => renderArmorCell(unit.sideArmor, unit.sideArmorType),
      }
    ),
    createSortableColumnWithAccessor(
      "rearArmor",
      (row) => row.rearArmor,
      "Rear Armor",
      {
        cell: (unit) => renderArmorCell(unit.rearArmor, unit.rearArmorType),
      }
    ),
    createSortableColumnWithAccessor(
      "topArmor",
      (row) => row.topArmor,
      "Top Armor",
      {
        cell: (unit) => renderArmorCell(unit.topArmor, unit.topArmorType),
      }
    ),
    createSortableColumn("era", "ERA", {
      cell: (value) => renderBoolean(value as boolean | undefined),
    }),
    createSortableColumn("maxDamage", "Max Damage"),
    
    // Movement
    createSortableColumn("speed", "Speed"),
    createSortableColumn("roadSpeed", "Road Speed"),
    createSortableColumn("rotationTime", "Rotation Time", {
      cell: (value) => formatTime(value as number | null | undefined),
    }),
    createSortableColumnWithAccessor(
      "movementType",
      (row) => row.movementType,
      "Movement Type",
      {
        cell: (unit) => renderEnum(unit.movementType, MovementType),
      }
    ),
    createSortableColumnWithAccessor(
      "occupiableTerrains",
      (row) => row.occupiableTerrains?.join(", ") || "",
      "Occupiable Terrains",
      {
        cell: (unit) => renderArray(unit.occupiableTerrains),
      }
    ),
    
    // Optics
    createSortableColumn("optics", "Optics"),
    createSortableColumn("airOptics", "Air Optics"),
    createSortableColumn("lowAltOptics", "Low Alt Optics"),
    createSortableColumn("opticsRange", "Optics Range", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    createSortableColumn("airOpticsRange", "Air Optics Range", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    createSortableColumn("lowAltOpticsRange", "Low Alt Optics Range", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    
    // Stealth & ECM
    createSortableColumn("stealth", "Stealth"),
    createSortableColumn("ecm", "ECM"),
    
    // Resources
    createSortableColumn("fuel", "Fuel"),
    createSortableColumn("fuelMove", "Fuel Move"),
    createSortableColumn("supply", "Supply"),
    createSortableColumn("advancedDeployment", "Advanced Deployment", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    
    // Other Stats
    createSortableColumn("agility", "Agility", {
      cell: (value) => renderOptionalValue(value as number | undefined),
    }),
    createSortableColumn("flyingAltitude", "Flying Altitude", {
      cell: (value) => formatDistance(value as number | null | undefined),
    }),
    createSortableColumn("travelTime", "Travel Time", {
      cell: (value) => formatTime(value as number | null | undefined),
    }),
    createSortableColumn("dangerousness", "Dangerousness", {
      cell: (value) => renderOptionalValue(value as number | undefined),
    }),
    
    // Special
    createSortableColumnWithAccessor(
      "specialities",
      (row) => {
        const traits = filterTraits(row.specialities || []);
        return traits.length > 0 ? traits.join(", ") : "-";
      },
      "Traits",
      {
        cell: (unit) => {
          const traits = filterTraits(unit.specialities || []);
          return traits.length > 0 ? (
            <TraitBadgeList specialities={traits} size="sm" />
          ) : (
            <span className="text-muted-foreground">-</span>
          );
        },
        filterFn: filterSpecialities,
      }
    ),
    createSortableColumn("bombStrategy", "Bomb Strategy", {
      cell: (value) => renderOptionalValue(value as string | undefined),
    }),
    createSortableColumn("xpBonuses", "XP Bonuses"),
    
    // Divisions
    createSortableColumnWithAccessor(
      "divisions",
      (row) => row.divisions?.join(", ") || "",
      "Divisions",
      {
        cell: (unit) => {
          if (!unit.divisions || unit.divisions.length === 0) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <div className="flex items-center gap-1 overflow-x-auto overflow-y-hidden max-w-full">
              {unit.divisions.map((divisionDescriptor) => (
                <Tooltip key={divisionDescriptor}>
                  <TooltipTrigger asChild>
                    <div className="w-6 h-6 cursor-help flex-shrink-0">
                      <img
                        src={getDivisionImagePath(divisionDescriptor)}
                        alt={divisionDescriptor}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{divisionDescriptor}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          );
        },
        filterFn: filterDivisions,
      }
    ),
    
    // Flags
    createSortableColumn("isCommand", "Is Command", {
      cell: (value) => renderBoolean(value as boolean | undefined),
    }),
    createSortableColumn("isSpecialForces", "Is Special Forces", {
      cell: (value) => renderBoolean(value as boolean),
    }),
    createSortableColumn("isSellable", "Is Sellable", {
      cell: (value) => renderBoolean(value as boolean),
    }),
  ];
}

